/**
 * Changing notification settings (2026-10-29): `PATCH
 * /users/@me/guilds/{guild}/settings`. The change shows at once (the
 * protocol's own merge, `applyGuildSettingsPatch`), then the server's answer
 * replaces it; a refusal puts back what was there and says why. Every other
 * session hears USER_GUILD_SETTINGS_UPDATE.
 */
import {
  applyGuildSettingsPatch,
  defaultGuildSettings,
  NotifyRoutes,
  type MuteDuration,
  type NotificationLevel,
  type PatchUserGuildSettingsRequest,
  type Snowflake,
  type UserGuildSettings,
} from '@neosocial/protocol';
import { api } from '../lib/api';
import { failToast } from '../mod/actions';
import { updateData, useData } from '../store/data';

/** The newest request per server wins: an older answer arriving late changes nothing. */
const latest = new Map<Snowflake, number>();
let seq = 0;

function put(guildId: Snowflake, v: UserGuildSettings | undefined): void {
  updateData((s) => {
    const guildSettings = { ...s.guildSettings };
    if (v) guildSettings[guildId] = v;
    else delete guildSettings[guildId];
    return { ...s, guildSettings };
  });
}

export async function patchGuildNotify(
  guildId: Snowflake,
  p: PatchUserGuildSettingsRequest,
): Promise<boolean> {
  const before = useData.getState().guildSettings[guildId];
  put(guildId, applyGuildSettingsPatch(before ?? defaultGuildSettings(guildId), p, Date.now()));
  const mine = ++seq;
  latest.set(guildId, mine);
  try {
    const out = await api.client.patch<UserGuildSettings>(NotifyRoutes.guildSettings(guildId), p);
    if (latest.get(guildId) === mine && out && typeof out === 'object') put(guildId, out);
    return true;
  } catch (e) {
    if (latest.get(guildId) === mine) put(guildId, before);
    failToast(e);
    return false;
  }
}

/** Fetches one server's settings (a server joined while connected: GUILD_CREATE). */
export async function loadGuildNotify(guildId: Snowflake): Promise<void> {
  try {
    const out = await api.client.get<UserGuildSettings>(NotifyRoutes.guildSettings(guildId));
    if (out && typeof out === 'object' && !latest.has(guildId)) put(guildId, out);
  } catch {
    /* the defaults until the next READY */
  }
}

/* ---- the shapes the menus and the dialog send ---- */

export const muteGuild = (g: Snowflake, w: MuteDuration) =>
  patchGuildNotify(g, { muted: true, mute_config: { selected_time_window: w } });
export const unmuteGuild = (g: Snowflake) => patchGuildNotify(g, { muted: false });
export const setGuildLevel = (g: Snowflake, level: NotificationLevel) =>
  patchGuildNotify(g, { message_notifications: level });

export const muteChannel = (g: Snowflake, c: Snowflake, w: MuteDuration) =>
  patchGuildNotify(g, {
    channel_overrides: { [c]: { muted: true, mute_config: { selected_time_window: w } } },
  });
export const unmuteChannel = (g: Snowflake, c: Snowflake) =>
  patchGuildNotify(g, { channel_overrides: { [c]: { muted: false } } });
export const setChannelLevel = (g: Snowflake, c: Snowflake, level: NotificationLevel) =>
  patchGuildNotify(g, { channel_overrides: { [c]: { message_notifications: level } } });
export const dropOverride = (g: Snowflake, c: Snowflake) =>
  patchGuildNotify(g, { channel_overrides: { [c]: null } });
